import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import { apiClient, createDraftSiteSpace } from '../../state';
import { useCreateInOwner } from '../ContentListing';

export interface OrganizationImportTarget {
    organization: string;
}

export interface SiteImportTarget extends OrganizationImportTarget {
    site: api.Site;
}

/** Import into a site that already has content: the space is added to it as a draft. */
export interface SiteContentImportTarget extends SiteImportTarget {
    siteStructure: api.SiteStructure;
}

export interface SiteOnboardingImportTarget extends SiteImportTarget {
    siteOnboarding: true;
}

export interface SpaceImportTarget {
    space: api.Space;
    changeRequest: string | undefined;
    page: string | undefined;
}

export interface StyleguideImportTarget extends SiteImportTarget {
    /**
     * Marks a style guide import: the site-owned `styleguide` space is created on
     * demand when the import starts, so cancelling never leaves an empty style guide.
     */
    styleguide: true;
}

export type ImportTarget =
    | StyleguideImportTarget
    | SiteOnboardingImportTarget
    | SiteContentImportTarget
    | SiteImportTarget
    | OrganizationImportTarget
    | SpaceImportTarget;

/**
 * Returns true if the target of the import is an organization.
 */
export function isOrganizationImportTarget(
    target: ImportTarget
): target is OrganizationImportTarget {
    return 'organization' in target;
}

export interface ImportResolvedTarget {
    space: api.Space;
    site?: api.Site | undefined;
    changeRequestId?: string | undefined;
    pageId?: string | undefined;
}

interface Context {
    /**
     * Function to get or create the space to import into.
     * If null, it means the user does not have the right to do this operation.
     */
    resolveTarget: (() => Promise<ImportResolvedTarget>) | null;
}

function SpaceImportTargetContext(props: {
    target: SpaceImportTarget;
    children: (ctx: Context) => React.ReactNode;
}) {
    const resolveTarget = useEventCallback(async () => ({
        space: props.target.space,
        changeRequestId: props.target.changeRequest,
        pageId: props.target.page,
    }));
    return props.children({ resolveTarget });
}

/**
 * Import into a new style guide: the site-owned `styleguide` space is only created
 * once the import actually starts, so cancelling the dialog leaves nothing behind.
 */
function StyleguideImportTargetContext(props: {
    target: StyleguideImportTarget;
    children: (ctx: Context) => React.ReactNode;
}) {
    const { target } = props;
    const resolveTarget = useEventCallback(async () => {
        // The endpoint is idempotent, so retrying a failed import reuses the same space.
        const { data: space } = await apiClient.sites.createSiteStyleguideById(
            target.organization,
            target.site.id
        );
        return { space, site: target.site };
    });
    return props.children({ resolveTarget });
}

function SiteOnboardingImportTargetContext(props: {
    target: SiteOnboardingImportTarget;
    children: (ctx: Context) => React.ReactNode;
}) {
    const { target } = props;
    const resolveTarget = useEventCallback(async () => {
        const { data: collection } = await apiClient.collections.createCollection(
            target.organization,
            {
                title: target.site.title,
            }
        );
        const { data: space } = await apiClient.spaces.createSpace(target.organization, {
            title: 'Docs',
            empty: true,
            parent: collection.id,
        });
        analytics.logEvent(
            analytics.createSpaceEvent(space, {
                type: 'space_create',
                source: 'import',
            })
        );
        const { data: changeRequest } = await apiClient.changeRequests.createChangeRequest(
            space.id,
            {
                subject: 'Import content',
            }
        );

        return {
            site: target.site,
            space,
            changeRequestId: changeRequest.id,
        };
    });
    return props.children({ resolveTarget });
}

function SiteContentImportTargetContext(props: {
    target: SiteContentImportTarget;
    children: (ctx: Context) => React.ReactNode;
}) {
    const { target } = props;
    // Kept across retries so a failure after the draft is created does not add another one.
    const draftSpaceRef = React.useRef<api.Space | null>(null);
    const resolveTarget = useEventCallback(async () => {
        let space = draftSpaceRef.current;
        if (!space) {
            space = await createDraftSiteSpace({
                organizationId: target.organization,
                site: target.site,
                siteStructure: target.siteStructure,
                title: 'Imported content',
            });
            draftSpaceRef.current = space;
            analytics.logEvent(
                analytics.createSpaceEvent(space, {
                    type: 'space_create',
                    source: 'import',
                })
            );
        }
        const { data: changeRequest } = await apiClient.changeRequests.createChangeRequest(
            space.id,
            { subject: 'Import content' }
        );
        return { site: target.site, space, changeRequestId: changeRequest.id };
    });
    return props.children({ resolveTarget });
}

/**
 * Modal to use when importing into a new space, in an organization.
 */
function OrganizationImportTargetContext(props: {
    target: OrganizationImportTarget | SiteImportTarget;
    children: (ctx: Context) => React.ReactNode;
}) {
    const { target } = props;
    const { createSpace } = useCreateInOwner({
        organization: target.organization,
    });
    const resolveTarget = useEventCallback(async () => {
        assert(createSpace, 'Permission denied should be caught earlier');
        const space = await createSpace({
            withTitle: 'site' in target ? 'Documentation' : undefined,
            withNavigate: false,
            source: 'import',
        });
        const { data: changeRequest } = await apiClient.changeRequests.createChangeRequest(
            space.id,
            { subject: 'Import content' }
        );
        return {
            site: 'site' in target ? target.site : undefined,
            space,
            changeRequestId: changeRequest.id,
        };
    });
    return props.children({ resolveTarget: createSpace ? resolveTarget : null });
}

export function ImportTargetContext(props: {
    target: ImportTarget;
    children: (ctx: Context) => React.ReactNode;
}) {
    const { target } = props;

    if ('styleguide' in target) {
        return (
            <StyleguideImportTargetContext target={target}>
                {props.children}
            </StyleguideImportTargetContext>
        );
    }

    if ('siteOnboarding' in target) {
        return (
            <SiteOnboardingImportTargetContext target={target}>
                {props.children}
            </SiteOnboardingImportTargetContext>
        );
    }

    if ('siteStructure' in target) {
        return (
            <SiteContentImportTargetContext target={target}>
                {props.children}
            </SiteContentImportTargetContext>
        );
    }

    if ('organization' in target) {
        return (
            <OrganizationImportTargetContext target={target}>
                {props.children}
            </OrganizationImportTargetContext>
        );
    }

    return <SpaceImportTargetContext target={target}>{props.children}</SpaceImportTargetContext>;
}
